import { stringify } from 'query-string';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';
import { MAX_LIMIT } from 'config';
import { EntityTypes, CustomRegionFilterType } from 'context/entityContext';
import getCountryIds from 'helpers/getCountryIds';

/**
 * This URL constructor creates a URL for radio spins over various types of radio demarcations.
 * @param {object} args URL constructor arguments
 * @param {array} args.countryIds Array of country id's to retrieve data for, if omitted data should be returned as broken down (split out) to each country separately
 * @param {boolean} args.download When this parameter is supplied as true the API returns a plain-text csv.
 * @param {array} args.entityIds Array of entity id's to retrieve data for
 * @param {number} args.formatId Filter for radio format id, if the filter is applied, it should return results for given id only.
 * @param {number} args.limit Number of items that should be returned in the response.
 * @param {number} args.marketId Filter for radio market id, if the filter is applied, it should return results for given id only.
 * @param {number} args.offset Offset for the returned selection of the result set.
 * @param {('track'|'artist'|'project')} args.entityType Entity type that supports showing data for radio
 * @param {('countThisWeekToDate'|'countLastWeekToDate'|'wowInProgress'|'countLastWeek'|'countLastLastWeek'|'wowLastCompleted')} args.sortBy What metric to sort the list by for radio spins
 * @param {('asc'|'desc')} args.sortOrder What order to sort the list by
 * @param {('tracks'|'stations'|'markets'|'countries')} args.spinsType The type of radio spins
 * @returns {string} Radio spins URL for tracks, stations, markets or countries
 */

export interface IRadioSpinsURLProps {
  countryIds?: string[] | string;
  download?: any;
  entityIds?: string[];
  formatId?: number;
  limit?: number;
  marketId?: string;
  offset?: number;
  entityType: EntityTypes;
  sortBy?: string;
  sortOrder?: string;
  spinsType?: string;
  region?: string;
  customRegion?: CustomRegionFilterType;
}

const getRadioSpinsURL = (args: IRadioSpinsURLProps): string => {
  const {
    countryIds,
    download,
    entityIds,
    formatId,
    limit = MAX_LIMIT,
    marketId,
    offset,
    entityType,
    sortBy,
    sortOrder,
    spinsType,
    region,
    customRegion,
  } = args;

  const apiEntityType = entityTypeToApiType(entityType);

  const params = stringify({
    countryIds: getCountryIds(countryIds, region, customRegion),
    download,
    formatId,
    limit,
    marketId,
    offset,
    sortBy,
    sortOrder,
    expand: 'percentage',
  });

  return `/api/radio/spins/${apiEntityType}/${entityIds}/${spinsType}${
    params !== '' ? `?${params}` : ''
  }`;
};

export default getRadioSpinsURL;
